import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { admin, type MonthlyRevenueRow, type ProgramRow, type CouponUsageRow, type TicketAnalytics } from "@/lib/admin";
import { AdminLayout } from "@/components/AdminLayout";
import { Card } from "@/components/Card";
import { LoadingScreen } from "@/components/LoadingScreen";
import { Alert } from "@/components/Alert";
import { MonthlyChart } from "@/components/MonthlyChart";

// ────────────────────────────────────────────────────────────────────────
// Formatters
// ────────────────────────────────────────────────────────────────────────

function momChange(now: number, prev: number): { label: string; tone: "green" | "gray" } {
  if (prev === 0) {
    if (now === 0) return { label: "no change", tone: "gray" };
    return { label: "new this month", tone: "green" };
  }
  const pct = ((now - prev) / prev) * 100;
  const arrow = pct >= 0 ? "↑" : "↓";
  return {
    label: `${arrow} ${Math.abs(pct).toFixed(0)}% vs last month`,
    tone: pct >= 0 ? "green" : "gray",
  };
}

function fmtUSD(cents: number): string {
  const d = cents / 100;
  if (d >= 1_000_000) return `$${(d / 1_000_000).toFixed(2)}M`;
  if (d >= 10_000) return `$${(d / 1000).toFixed(1)}k`;
  return `$${d.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function monthShort(yyyyMm: string): string {
  // "2026-05" → "May"
  const [y, m] = yyyyMm.split("-").map(Number);
  if (!y || !m) return yyyyMm;
  return new Date(y, m - 1, 1).toLocaleString("en-US", { month: "short" });
}

// ────────────────────────────────────────────────────────────────────────
// Drilldown state — which tile is "active"
// ────────────────────────────────────────────────────────────────────────

type Drilldown = "total" | "new" | "lost" | "churned" | null;

// ────────────────────────────────────────────────────────────────────────

export default function AdminAnalytics() {
  const [drilldown, setDrilldown] = useState<Drilldown>(null);

  const analyticsQ = useQuery({
    queryKey: ["admin", "analytics"],
    queryFn: () => admin.getAnalytics(),
  });
  const programsQ = useQuery({
    queryKey: ["admin", "usersByProgram"],
    queryFn: () => admin.getUsersByProgram(),
  });
  const revenueQ = useQuery({
    queryKey: ["admin", "revenue"],
    queryFn: () => admin.getRevenue(12),
  });
  const couponsQ = useQuery({
    queryKey: ["admin", "couponUsage"],
    queryFn: () => admin.getCouponUsage(12),
  });
  const ticketsQ = useQuery({
    queryKey: ["admin", "ticketAnalytics"],
    queryFn: () => admin.getTicketAnalytics(),
    staleTime: 30_000,
  });

  if (analyticsQ.isLoading) return <AdminLayout><LoadingScreen /></AdminLayout>;
  if (analyticsQ.error) {
    return (
      <AdminLayout>
        <Alert tone="error">{(analyticsQ.error as Error).message}</Alert>
      </AdminLayout>
    );
  }
  const a = analyticsQ.data!;
  const newMom = momChange(a.new_this_month, a.new_last_month);
  const delMom = momChange(a.deleted_this_month, a.deleted_last_month);

  return (
    <AdminLayout>
      <header className="mb-5">
        <h1 className="font-serif text-[28px] text-fh-ink leading-tight mb-1">Analytics</h1>
        <p className="text-[14px] text-fh-gray m-0">
          User growth, program mix, revenue, and coupon usage. Click any tile for the breakdown.
        </p>
      </header>

      {/* Clickable stat tiles */}
      <div className="flex gap-3.5 mb-4 flex-wrap">
        <ClickableTile
          label="Total users"
          value={a.total_users.toLocaleString()}
          hint={`${a.new_30d.toLocaleString()} added in last 30 days`}
          selected={drilldown === "total"}
          onClick={() => setDrilldown(drilldown === "total" ? null : "total")}
        />
        <ClickableTile
          label="New this month"
          value={a.new_this_month.toLocaleString()}
          hint={newMom.label}
          hintTone={newMom.tone}
          selected={drilldown === "new"}
          onClick={() => setDrilldown(drilldown === "new" ? null : "new")}
        />
        <ClickableTile
          label="Lost this month"
          value={a.deleted_this_month.toLocaleString()}
          hint={delMom.label}
          hintTone="gray"
          selected={drilldown === "lost"}
          onClick={() => setDrilldown(drilldown === "lost" ? null : "lost")}
        />
        <ClickableTile
          label="Total churned"
          value={a.deleted_total.toLocaleString()}
          hint="all-time admin deletes + archives"
          hintTone="gray"
          selected={drilldown === "churned"}
          onClick={() => setDrilldown(drilldown === "churned" ? null : "churned")}
        />
      </div>

      {/* Drilldown report panel — appears when a tile is clicked. */}
      {drilldown && (
        <Card title={drilldownTitle(drilldown)} action={
          <button
            type="button"
            onClick={() => setDrilldown(null)}
            className="text-[12px] font-semibold text-fh-gray hover:text-fh-ink"
          >
            Close
          </button>
        }>
          <DrilldownReport drilldown={drilldown} analytics={a} programs={programsQ.data?.programs ?? []} />
        </Card>
      )}

      {/* Existing 12-month new/deleted chart */}
      <div className="mt-4">
        <Card
          title="New vs. lost — last 12 months"
          action={`${a.monthly.reduce((s, m) => s + m.new, 0)} new · ${a.monthly.reduce((s, m) => s + m.deleted, 0)} deleted`}
        >
          <MonthlyChart data={a.monthly} />
        </Card>
      </div>

      {/* NEW: Users by program */}
      <div className="mt-4">
        <Card title="Users by program" action={programsQ.isLoading ? "Loading…" : undefined}>
          {programsQ.error ? (
            <Alert tone="error">{(programsQ.error as Error).message}</Alert>
          ) : (
            <ProgramBars rows={programsQ.data?.programs ?? []} />
          )}
        </Card>
      </div>

      {/* NEW: Month-over-month revenue tracker */}
      <div className="mt-4">
        <Card title="Monthly recurring revenue — last 12 months" action={
          revenueQ.data?.months?.length
            ? fmtUSD(revenueQ.data.months[revenueQ.data.months.length - 1]?.mrr_cents ?? 0) + " current"
            : revenueQ.isLoading ? "Loading…" : undefined
        }>
          {revenueQ.error ? (
            <Alert tone="error">{(revenueQ.error as Error).message}</Alert>
          ) : (
            <RevenueChart rows={revenueQ.data?.months ?? []} />
          )}
        </Card>
      </div>

      {/* NEW: Coupon usage by code+month */}
      <div className="mt-4">
        <Card title="Coupon usage" action={couponsQ.isLoading ? "Loading…" : undefined}>
          {couponsQ.error ? (
            <Alert tone="error">{(couponsQ.error as Error).message}</Alert>
          ) : (
            <CouponTable rows={couponsQ.data?.usage ?? []} />
          )}
        </Card>
      </div>

      <TicketsSection
        data={ticketsQ.data}
        isLoading={ticketsQ.isLoading}
        error={ticketsQ.error as Error | null}
      />

      <p className="text-center text-[11.5px] text-fh-gray mt-6">
        Revenue is derived from active treatments × monthly_price.
        Archived patients are excluded from active counts.
        Once Stripe is wired, MRR will switch to invoice-paid totals.
      </p>
    </AdminLayout>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Tickets section — ticketing metrics from admin-ticket-analytics.
// ────────────────────────────────────────────────────────────────────────

function TicketsSection({
  data, isLoading, error,
}: { data: TicketAnalytics | undefined; isLoading: boolean; error: Error | null }) {
  if (error) {
    return (
      <div className="mt-4">
        <Card title="Tickets">
          <Alert tone="error">{error.message}</Alert>
        </Card>
      </div>
    );
  }
  if (isLoading || !data) {
    return (
      <div className="mt-4">
        <Card title="Tickets" action="Loading…">
          <div className="h-[80px]" />
        </Card>
      </div>
    );
  }

  const { current, month_over_month: mom, monthly, by_tag } = data;
  const createdMom = pctLabel(mom.pct_change_created, mom.created_this_month);
  const resolvedMom = pctLabel(mom.pct_change_resolved, mom.resolved_this_month);

  return (
    <>
      <div className="mt-5">
        <h2 className="font-serif text-[20px] text-fh-ink mb-2">Tickets</h2>
        <div className="flex gap-3.5 mb-4 flex-wrap">
          <StaticTile label="Open" value={current.open.toLocaleString()} hint="status = open" hintTone="green" />
          <StaticTile
            label="Awaiting patient"
            value={current.awaiting_patient.toLocaleString()}
            hint="we replied, waiting on them"
            hintTone="gray"
          />
          <StaticTile
            label="Unassigned · active"
            value={current.unassigned_active.toLocaleString()}
            hint="needs a clinician picked"
            hintTone={current.unassigned_active > 0 ? "green" : "gray"}
          />
          <StaticTile
            label="Resolved · all-time"
            value={current.resolved_total.toLocaleString()}
            hint="closed threads"
            hintTone="gray"
          />
        </div>

        {/* Month-over-month tiles */}
        <div className="flex gap-3.5 mb-4 flex-wrap">
          <StaticTile
            label="Created this month"
            value={mom.created_this_month.toLocaleString()}
            hint={createdMom.label}
            hintTone={createdMom.tone}
          />
          <StaticTile
            label="Resolved this month"
            value={mom.resolved_this_month.toLocaleString()}
            hint={resolvedMom.label}
            hintTone={resolvedMom.tone}
          />
          <StaticTile
            label="Created last month"
            value={mom.created_last_month.toLocaleString()}
            hint="for comparison"
            hintTone="gray"
          />
          <StaticTile
            label="Resolved last month"
            value={mom.resolved_last_month.toLocaleString()}
            hint="for comparison"
            hintTone="gray"
          />
        </div>
      </div>

      <div className="mt-2">
        <Card
          title="Ticket volume — last 12 months"
          action={`${monthly.reduce((s, m) => s + m.created, 0)} created · ${monthly.reduce((s, m) => s + m.resolved, 0)} resolved`}
        >
          <TicketMonthlyChart data={monthly} />
        </Card>
      </div>

      <div className="mt-4">
        <Card title="Tickets by tag" action={by_tag.length === 0 ? "No tags yet" : `${by_tag.length} tags`}>
          {by_tag.length === 0 ? (
            <p className="text-[13px] text-fh-gray m-0">
              Tag threads from the admin /messages dashboard — counts appear here.
            </p>
          ) : (
            <TagBars rows={by_tag} />
          )}
        </Card>
      </div>

      {/* Time to close (this month vs all-time) */}
      <div className="mt-4 flex gap-3.5 flex-wrap">
        <StaticTile
          label="Avg time to close · this month"
          value={fmtHoursShort(data.time_to_close.this_month.avg_hours)}
          hint={`${data.time_to_close.this_month.count.toLocaleString()} resolved`}
          hintTone="gray"
        />
        <StaticTile
          label="Median time to close · this month"
          value={fmtHoursShort(data.time_to_close.this_month.median_hours)}
          hint="50th percentile this month"
          hintTone="gray"
        />
        <StaticTile
          label="Avg time to close · all-time"
          value={fmtHoursShort(data.time_to_close.all_time.avg_hours)}
          hint={`${data.time_to_close.all_time.count.toLocaleString()} resolved`}
          hintTone="gray"
        />
        <StaticTile
          label="Median time to close · all-time"
          value={fmtHoursShort(data.time_to_close.all_time.median_hours)}
          hint="50th percentile"
          hintTone="gray"
        />
      </div>

      {/* Resolved by admin leaderboard */}
      <div className="mt-4">
        <Card
          title="Resolved by admin"
          action={data.by_admin.length === 0 ? "No resolutions yet" : `${data.by_admin.length} admins`}
        >
          {data.by_admin.length === 0 ? (
            <p className="text-[13px] text-fh-gray m-0">
              Once admins start closing tickets, the leaderboard shows up here.
            </p>
          ) : (
            <ResolvedByAdminBars rows={data.by_admin} />
          )}
        </Card>
      </div>
    </>
  );
}

// Compact "1.2h" / "3d 4h" / "—" formatter for the time-to-close tiles.
function fmtHoursShort(h: number | null): string {
  if (h === null || !Number.isFinite(h)) return "—";
  if (h < 1) {
    const m = Math.round(h * 60);
    return `${m}m`;
  }
  if (h < 24) return `${h.toFixed(1)}h`;
  const days = Math.floor(h / 24);
  const rem = Math.round(h - days * 24);
  return rem > 0 ? `${days}d ${rem}h` : `${days}d`;
}

function ResolvedByAdminBars({
  rows,
}: { rows: Array<{ display_name: string; email: string | null; count: number }> }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <ul className="flex flex-col gap-2">
      {rows.map((r, i) => (
        <li key={`${r.display_name}-${i}`} className="flex items-center gap-3 text-[13px]">
          <span className="w-[26px] text-right text-fh-gray-light font-mono text-[11px]">
            {i + 1}.
          </span>
          <span className="w-[180px] truncate">
            <span className="font-semibold text-fh-ink">{r.display_name}</span>
            {r.email && r.email !== r.display_name && (
              <span className="text-[11px] text-fh-gray ml-1">{r.email}</span>
            )}
          </span>
          <div className="flex-1 bg-fh-sand rounded-full h-2 overflow-hidden">
            <div
              className="h-full rounded-full bg-fh-green-dark"
              style={{ width: `${(r.count / max) * 100}%` }}
            />
          </div>
          <span className="w-[40px] text-right font-semibold text-fh-ink">{r.count}</span>
        </li>
      ))}
    </ul>
  );
}

function pctLabel(pct: number | null, currentValue: number): { label: string; tone: "green" | "gray" } {
  if (pct === null) {
    return currentValue === 0
      ? { label: "no change", tone: "gray" }
      : { label: "new this month", tone: "green" };
  }
  if (pct === 0) return { label: "no change", tone: "gray" };
  const arrow = pct >= 0 ? "↑" : "↓";
  return {
    label: `${arrow} ${Math.abs(pct).toFixed(0)}% vs last month`,
    tone: pct >= 0 ? "green" : "gray",
  };
}

// Twin-bar chart: created (green) vs resolved (sage) per month.
function TicketMonthlyChart({
  data,
}: { data: Array<{ month: string; created: number; resolved: number }> }) {
  const max = Math.max(1, ...data.flatMap((d) => [d.created, d.resolved]));
  return (
    <div className="flex items-end justify-between gap-2 h-[140px] pt-2">
      {data.map((d) => {
        const cH = Math.round((d.created / max) * 110);
        const rH = Math.round((d.resolved / max) * 110);
        return (
          <div key={d.month} className="flex-1 flex flex-col items-center gap-1">
            <div className="flex items-end gap-0.5 h-[110px]">
              <div
                title={`Created: ${d.created}`}
                className="w-2.5 rounded-t bg-fh-green-dark"
                style={{ height: `${cH || 2}px` }}
              />
              <div
                title={`Resolved: ${d.resolved}`}
                className="w-2.5 rounded-t bg-fh-sage border border-fh-green/30"
                style={{ height: `${rH || 2}px` }}
              />
            </div>
            <div className="text-[10px] text-fh-gray">{monthShort(d.month)}</div>
          </div>
        );
      })}
      <div className="ml-2 flex flex-col gap-1 text-[10.5px] text-fh-gray w-[80px] shrink-0">
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-fh-green-dark" />Created
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-fh-sage border border-fh-green/30" />Resolved
        </div>
      </div>
    </div>
  );
}

// Horizontal bar list for tags.
function TagBars({ rows }: { rows: Array<{ name: string; color: string | null; count: number }> }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <ul className="flex flex-col gap-2">
      {rows.map((r) => (
        <li key={r.name} className="flex items-center gap-3 text-[13px]">
          <span
            className="w-2.5 h-2.5 rounded-full shrink-0"
            style={{ backgroundColor: r.color ? `#${r.color}` : "#888" }}
          />
          <span className="w-[140px] truncate text-fh-ink">{r.name}</span>
          <div className="flex-1 bg-fh-sand rounded-full h-2 overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{
                width: `${(r.count / max) * 100}%`,
                backgroundColor: r.color ? `#${r.color}` : "#525252",
              }}
            />
          </div>
          <span className="w-[40px] text-right font-semibold text-fh-ink">{r.count}</span>
        </li>
      ))}
    </ul>
  );
}

// Non-clickable variant of the existing ClickableTile (kept lightweight
// so it doesn't interfere with the User analytics tiles' drilldown).
function StaticTile({
  label, value, hint, hintTone = "gray",
}: { label: string; value: string; hint?: string; hintTone?: "green" | "gray" }) {
  return (
    <div className="bg-white border border-fh-border rounded-[12px] p-4 flex-1 min-w-[180px]">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-fh-gray">{label}</div>
      <div className="text-[26px] font-serif text-fh-ink mt-1 leading-none">{value}</div>
      {hint && (
        <div className={`text-[11.5px] mt-1.5 ${hintTone === "green" ? "text-fh-green-dark font-semibold" : "text-fh-gray"}`}>
          {hint}
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// ClickableTile — variant of StatTile that toggles a drilldown.
// ────────────────────────────────────────────────────────────────────────

function ClickableTile({
  label, value, hint, hintTone = "green", selected, onClick,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  hintTone?: "green" | "gray";
  selected: boolean;
  onClick: () => void;
}) {
  const hintColor = hintTone === "green" ? "text-fh-green" : "text-fh-gray";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 min-w-[160px] text-left bg-white border rounded-[14px] px-[18px] py-4 transition cursor-pointer ${
        selected
          ? "border-fh-green ring-2 ring-fh-green/30"
          : "border-fh-border hover:border-fh-green/50 hover:shadow-card"
      }`}
    >
      <div className="text-[12.5px] text-fh-gray mb-1.5 flex items-center justify-between">
        {label}
        <span className="text-fh-gray-light text-[10.5px] uppercase tracking-wider">
          {selected ? "—" : "view →"}
        </span>
      </div>
      <div className="font-serif text-[24px] text-fh-green-dark leading-tight">{value}</div>
      {hint && <div className={`mt-1 text-[12px] ${hintColor}`}>{hint}</div>}
    </button>
  );
}

function drilldownTitle(d: Drilldown): string {
  switch (d) {
    case "total":   return "Total users — program mix";
    case "new":     return "New sign-ups this month";
    case "lost":    return "Lost this month (archived + deleted)";
    case "churned": return "All-time churn — monthly breakdown";
    default:        return "";
  }
}

function DrilldownReport({
  drilldown, analytics, programs,
}: {
  drilldown: NonNullable<Drilldown>;
  analytics: ReturnType<typeof useDummy>;
  programs: ProgramRow[];
}) {
  if (drilldown === "total") {
    return <ProgramBars rows={programs} />;
  }
  if (drilldown === "new") {
    return (
      <div>
        <p className="text-[14px] text-fh-ink leading-relaxed">
          <strong>{analytics.new_this_month}</strong> new sign-ups this calendar month.
          {" "}({analytics.new_last_month} last month · {analytics.new_30d} in last 30 days.)
        </p>
        <p className="text-[13px] text-fh-gray mt-2">
          For a row-level list, head to the Users tab and sort by "Created (newest)".
        </p>
      </div>
    );
  }
  if (drilldown === "lost") {
    return (
      <div>
        <p className="text-[14px] text-fh-ink leading-relaxed">
          <strong>{analytics.deleted_this_month}</strong> accounts archived or deleted this month.
          {" "}({analytics.deleted_last_month} last month · {analytics.deleted_total} all-time.)
        </p>
        <p className="text-[13px] text-fh-gray mt-2">
          For row-level detail, go to the Users tab → toggle "Show archived" and sort by "Created (newest)".
          Reasons captured at archive time appear in the audit log (access_log table).
        </p>
      </div>
    );
  }
  // churned
  return (
    <div className="text-[13px]">
      <p className="text-fh-gray mb-3">Lost users per month (last 12):</p>
      <div className="space-y-1.5">
        {analytics.monthly.map((m) => (
          <div key={m.month} className="flex items-center gap-3">
            <span className="w-16 text-fh-gray">{monthShort(m.month)}</span>
            <div className="flex-1 bg-fh-cream rounded-full h-2 overflow-hidden">
              <div
                className="h-full bg-fh-gray"
                style={{ width: `${Math.min(100, m.deleted * 10)}%` }}
              />
            </div>
            <span className="w-10 text-right text-fh-ink font-semibold">{m.deleted}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// helper for type inference
function useDummy() { return null as unknown as import("@/lib/admin").AdminAnalytics; }

// ────────────────────────────────────────────────────────────────────────
// ProgramBars — horizontal bar chart of users by program
// ────────────────────────────────────────────────────────────────────────

function ProgramBars({ rows }: { rows: ProgramRow[] }) {
  if (rows.length === 0) {
    return <p className="text-[13px] text-fh-gray italic">No treatments recorded yet.</p>;
  }
  const max = Math.max(...rows.map((r) => r.user_count), 1);
  return (
    <div className="space-y-2.5">
      {rows.map((r) => (
        <div key={r.program} className="flex items-center gap-3">
          <span className="w-[180px] text-[13px] text-fh-ink truncate" title={r.program}>{r.program}</span>
          <div className="flex-1 bg-fh-cream rounded-full h-3 overflow-hidden relative">
            <div
              className="h-full bg-fh-green-dark"
              style={{ width: `${(r.user_count / max) * 100}%` }}
            />
            {r.active_count !== r.user_count && (
              <div
                className="absolute top-0 left-0 h-full bg-fh-green"
                style={{ width: `${(r.active_count / max) * 100}%` }}
                title={`${r.active_count} active`}
              />
            )}
          </div>
          <span className="w-[110px] text-right text-[13px] text-fh-ink">
            <strong>{r.user_count}</strong>
            <span className="text-fh-gray ml-1">({r.active_count} active)</span>
          </span>
        </div>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// RevenueChart — bars per month for MRR
// ────────────────────────────────────────────────────────────────────────

function RevenueChart({ rows }: { rows: MonthlyRevenueRow[] }) {
  if (rows.length === 0) {
    return <p className="text-[13px] text-fh-gray italic">No revenue data yet.</p>;
  }
  const max = Math.max(...rows.map((r) => r.mrr_cents), 1);
  return (
    <div>
      <div className="flex items-end gap-2 h-[160px] mb-2">
        {rows.map((r) => {
          const pct = max > 0 ? (r.mrr_cents / max) * 100 : 0;
          return (
            <div key={r.month} className="flex-1 flex flex-col items-center gap-1 group relative">
              <div className="flex-1 w-full flex items-end">
                <div
                  className="w-full bg-fh-green hover:bg-fh-green-dark rounded-t-md transition"
                  style={{ height: `${pct}%`, minHeight: r.mrr_cents > 0 ? "4px" : "0" }}
                  title={`${monthShort(r.month)}: ${fmtUSD(r.mrr_cents)} MRR · ${r.active_subscriptions} active`}
                />
              </div>
              <span className="text-[10.5px] text-fh-gray">{monthShort(r.month)}</span>
            </div>
          );
        })}
      </div>
      <div className="grid grid-cols-3 gap-3 text-center text-[12px] mt-3 pt-3 border-t border-fh-border">
        <div>
          <div className="text-fh-gray">Current MRR</div>
          <div className="font-serif text-[18px] text-fh-green-dark">
            {fmtUSD(rows[rows.length - 1]?.mrr_cents ?? 0)}
          </div>
        </div>
        <div>
          <div className="text-fh-gray">New this month</div>
          <div className="font-serif text-[18px] text-fh-ink">
            {rows[rows.length - 1]?.new_subscriptions ?? 0}
          </div>
        </div>
        <div>
          <div className="text-fh-gray">Canceled this month</div>
          <div className="font-serif text-[18px] text-fh-gray">
            {rows[rows.length - 1]?.canceled_subscriptions ?? 0}
          </div>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// CouponTable — by code + by month
// ────────────────────────────────────────────────────────────────────────

function CouponTable({ rows }: { rows: CouponUsageRow[] }) {
  if (rows.length === 0) {
    return (
      <div className="text-[13px] text-fh-gray italic">
        No coupon redemptions yet. Once a coupon is used at checkout, redemptions land here grouped
        by code and month.
      </div>
    );
  }
  // Pivot: rows by code, columns by month
  const codes = Array.from(new Set(rows.map((r) => r.coupon_code))).sort();
  const months = Array.from(new Set(rows.map((r) => r.month))).sort();
  const lookup = new Map(rows.map((r) => [`${r.coupon_code}|${r.month}`, r]));

  // Per-code totals
  const codeTotals = codes.map((c) => ({
    code: c,
    count: rows.filter((r) => r.coupon_code === c).reduce((s, r) => s + r.redemption_count, 0),
    cents: rows.filter((r) => r.coupon_code === c).reduce((s, r) => s + r.total_discount_cents, 0),
  }));

  return (
    <div className="overflow-x-auto -mx-[22px] -my-[10px]">
      <table className="w-full text-[12.5px]">
        <thead className="text-left text-[11px] uppercase tracking-wider text-fh-gray bg-fh-cream border-b border-fh-border">
          <tr>
            <th className="px-4 py-2.5 font-semibold sticky left-0 bg-fh-cream z-10">Code</th>
            {months.map((m) => (
              <th key={m} className="px-3 py-2.5 font-semibold text-right">{monthShort(m)}</th>
            ))}
            <th className="px-4 py-2.5 font-semibold text-right">Total</th>
            <th className="px-4 py-2.5 font-semibold text-right">$ off</th>
          </tr>
        </thead>
        <tbody>
          {codes.map((c) => {
            const totals = codeTotals.find((t) => t.code === c)!;
            return (
              <tr key={c} className="border-b border-fh-border last:border-0 hover:bg-fh-cream/60">
                <td className="px-4 py-2 font-mono font-semibold text-fh-ink sticky left-0 bg-white z-10">{c}</td>
                {months.map((m) => {
                  const r = lookup.get(`${c}|${m}`);
                  return (
                    <td key={m} className="px-3 py-2 text-right text-fh-ink">
                      {r?.redemption_count ?? "—"}
                    </td>
                  );
                })}
                <td className="px-4 py-2 text-right font-semibold text-fh-ink">{totals.count}</td>
                <td className="px-4 py-2 text-right text-fh-gray">{fmtUSD(totals.cents)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
